// src/components/LandingPage.js
"use client";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  AppBar, Toolbar, Container, Box, Button, Typography, Stack, Grid, Paper, Chip, Divider, Link as MUILink,
  IconButton
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ThemeToggleButton from "../components/ThemeToggleButton";

// Icons
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import LinkIcon from "@mui/icons-material/Link";
import DescriptionIcon from "@mui/icons-material/Description";
import ImageIcon from "@mui/icons-material/Image";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import BoltIcon from "@mui/icons-material/Bolt";
import SearchIcon from "@mui/icons-material/Search";
import ShieldIcon from "@mui/icons-material/Shield";
import StorageIcon from "@mui/icons-material/Storage";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import PsychologyIcon from "@mui/icons-material/Psychology";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import GavelIcon from "@mui/icons-material/Gavel";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import SchoolIcon from "@mui/icons-material/School";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import VisibilityIcon from "@mui/icons-material/Visibility";      // viewer
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline"; // audio/video
import YouTubeIcon from "@mui/icons-material/YouTube";           // YouTube
import FeedbackIcon from "@mui/icons-material/Feedback";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import SettingsEthernetIcon from "@mui/icons-material/SettingsEthernet";
import CloudQueueIcon from "@mui/icons-material/CloudQueue";



const LOGIN_URL = process.env.NEXT_PUBLIC_LOGIN_URL || ""; // optional external SSO/IdP

/* =====================================================
   ScreenshotCarousel (hero) — compact slider
   ===================================================== */
function ScreenshotCarousel({
  images = [],
  interval = 3000,
  aspect = "66%", // approximate 3:2 (height = 66% of width)
  rounded = 16,
}) {
  const [index, setIndex] = useState(0);
  const timerRef = useRef(null);
  const hoverRef = useRef(false);
  const startXRef = useRef(null);

  const count = images.length;
  const safeIndex = (n) => (n + count) % count;

  const next = () => setIndex((i) => safeIndex(i + 1));
  const prev = () => setIndex((i) => safeIndex(i - 1));
  const goto = (i) => setIndex(safeIndex(i));

  useEffect(() => {
    if (count <= 1) return;
    timerRef.current = setInterval(() => {
      if (!hoverRef.current) next();
    }, interval);
    return () => clearInterval(timerRef.current);
  }, [interval, count]);

  const onEnter = () => { hoverRef.current = true; };
  const onLeave = () => { hoverRef.current = false; };

  const onTouchStart = (e) => { startXRef.current = e.touches?.[0]?.clientX ?? null; };
  const onTouchEnd = (e) => {
    const endX = e.changedTouches?.[0]?.clientX ?? null;
    if (startXRef.current == null || endX == null) return;
    const dx = endX - startXRef.current;
    if (Math.abs(dx) > 30) (dx < 0 ? next() : prev());
    startXRef.current = null;
  };

  if (count === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 2, borderRadius: rounded }}>
        <Typography variant="body2" color="text.secondary">
          Add screenshots in <code>public/screenshots/</code> and update the <b>SCREENSHOTS</b> array.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      sx={{
        position: "relative",
        overflow: "hidden",
        borderRadius: rounded,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: (t) => alpha(t.palette.background.paper, 0.7),
        backdropFilter: "blur(6px)",
      }}
    >
      <Box sx={{ position: "relative", width: "100%", pt: aspect }}>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            width: `${count * 100}%`,
            transform: `translateX(-${(index * 100) / count}%)`,
            transition: "transform .6s cubic-bezier(.22,.61,.36,1)",
          }}
        >
          {images.map((src, i) => (
            <Box key={src + i} sx={{ width: `${100 / count}%`, position: "relative", p: { xs: 0.5, sm: 1.25 } }}>
              <Box
                component="img"
                src={src}
                alt={`Screenshot ${i + 1}`}
                sx={{ display: "block", width: "100%", height: "100%", objectFit: "cover", borderRadius: 2, boxShadow: 1 }}
                loading={i === 0 ? "eager" : "lazy"}
              />
            </Box>
          ))}
        </Box>

        {/* Edge gradients */}
        <Box
          sx={{
            pointerEvents: "none",
            position: "absolute",
            inset: 0,
            "&:before, &:after": {
              content: '""',
              position: "absolute",
              top: 0, bottom: 0,
              width: 48, zIndex: 1,
            },
            "&:before": {
              left: 0,
              background: (t) => `linear-gradient(90deg, ${alpha(t.palette.background.paper, 0.9)} 0%, transparent 100%)`,
            },
            "&:after": {
              right: 0,
              background: (t) => `linear-gradient(270deg, ${alpha(t.palette.background.paper, 0.9)} 0%, transparent 100%)`,
            },
          }}
        />

        {/* Arrows */}
        <IconButton
          aria-label="Previous"
          onClick={prev}
          sx={(t) => ({
            position: "absolute",
            top: "50%",
            left: 8,
            transform: "translateY(-50%)",
            zIndex: 2,
            bgcolor: alpha(t.palette.background.paper, 0.9),
            border: "1px solid",
            borderColor: "divider",
            "&:hover": { bgcolor: alpha(t.palette.background.paper, 1) },
          })}
        >
          <ChevronLeftIcon />
        </IconButton>
        <IconButton
          aria-label="Next"
          onClick={next}
          sx={(t) => ({
            position: "absolute",
            top: "50%",
            right: 8,
            transform: "translateY(-50%)",
            zIndex: 2,
            bgcolor: alpha(t.palette.background.paper, 0.9),
            border: "1px solid",
            borderColor: "divider",
            "&:hover": { bgcolor: alpha(t.palette.background.paper, 1) },
          })}
        >
          <ChevronRightIcon />
        </IconButton>

        {/* Dots */}
        <Stack
          direction="row"
          spacing={0.75}
          sx={{
            position: "absolute",
            bottom: 8,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 2,
            bgcolor: (t) => alpha(t.palette.background.paper, 0.7),
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 999,
            px: 1,
            py: 0.25,
            alignItems: "center",
          }}
        >
          {images.map((_, i) => (
            <IconButton
              key={`dot-${i}`}
              size="small"
              onClick={() => goto(i)}
              sx={{ p: 0.5, opacity: i === index ? 1 : 0.55 }}
              aria-label={`Go to slide ${i + 1}`}
            >
              <FiberManualRecordIcon fontSize="inherit" />
            </IconButton>
          ))}
        </Stack>
      </Box>
    </Paper>
  );
}

/* =====================================================
   SpotlightSlider — large, animated showcase
   ===================================================== */
function SpotlightSlider({ images = [], interval = 3500 }) {
  const [index, setIndex] = useState(0);
  const count = images.length;
  const timerRef = useRef(null);
  const hoverRef = useRef(false);
  const startXRef = useRef(null);

  const next = () => setIndex((i) => (i + 1) % count);
  const prev = () => setIndex((i) => (i - 1 + count) % count);
  const goto = (i) => setIndex(((i % count) + count) % count);

  useEffect(() => {
    if (count <= 1) return;
    timerRef.current = setInterval(() => {
      if (!hoverRef.current) next();
    }, interval);
    return () => clearInterval(timerRef.current);
  }, [interval, count]);

  const onEnter = () => { hoverRef.current = true; };
  const onLeave = () => { hoverRef.current = false; };

  const onTouchStart = (e) => { startXRef.current = e.touches?.[0]?.clientX ?? null; };
  const onTouchEnd = (e) => {
    const endX = e.changedTouches?.[0]?.clientX ?? null;
    if (startXRef.current == null || endX == null) return;
    const dx = endX - startXRef.current;
    if (Math.abs(dx) > 36) (dx < 0 ? next() : prev());
    startXRef.current = null;
  };

  if (count === 0) return null;

  const rel = (i) => {
    let d = i - index;
    if (d > count / 2) d -= count;
    if (d < -count / 2) d += count;
    return d;
  };

  return (
    <Box
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      sx={{
        position: "relative",
        borderRadius: 3,
        overflow: "visible",
        py: { xs: 4, md: 6 },
        px: { xs: 1, md: 2 },
        background: (t) =>
          `radial-gradient(80% 60% at 50% 0%, ${alpha(t.palette.primary.main, 0.06)} 0%, transparent 70%)`,
      }}
    >
      <style>{`
        @keyframes kenburns {
          0% { transform: scale(1) translate3d(0,0,0); }
          100% { transform: scale(1.06) translate3d(0, -1%, 0); }
        }
      `}</style>

      <Box
        sx={{
          position: "relative",
          height: { xs: 260, sm: 340, md: 480 },
          maxWidth: 1200,
          mx: "auto",
          perspective: 1200,
        }}
      >
        {images.map((src, i) => {
          const d = rel(i);
          const isActive = d === 0;
          const translateX = d * 28;
          const scale = isActive ? 1 : Math.max(0.82, 1 - Math.abs(d) * 0.12);
          const z = 100 - Math.abs(d);

          return (
            <Box
              key={`${src}-${i}`}
              onClick={() => goto(i)}
              sx={{
                cursor: isActive ? "default" : "pointer",
                userSelect: "none",
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: `translate(-50%, -50%) translateX(${translateX}%) scale(${scale})`,
                transition: "transform .8s cubic-bezier(.2,.8,.2,1)",
                zIndex: z,
                filter: isActive ? "none" : "blur(0.3px)",
              }}
            >
              <Paper
                elevation={isActive ? 8 : 2}
                sx={{
                  overflow: "hidden",
                  borderRadius: 2.5,
                  border: "1px solid",
                  borderColor: "divider",
                  width: { xs: 260, sm: 440, md: 760 },
                  height: { xs: 160, sm: 260, md: 440 },
                  boxShadow: (t) =>
                    isActive
                      ? `0 14px 40px ${alpha(t.palette.primary.main, 0.25)}`
                      : `0 6px 18px ${alpha(t.palette.common.black, 0.18)}`,
                  bgcolor: (t) => alpha(t.palette.background.paper, 0.9),
                }}
              >
                <Box
                  component="img"
                  src={src}
                  alt={`Screenshot ${i + 1}`}
                  loading={i === 0 ? "eager" : "lazy"}
                  draggable={false}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                    transformOrigin: "center",
                    animation: isActive ? "kenburns 10s ease-in-out both" : "none",
                  }}
                />
              </Paper>
            </Box>
          );
        })}

        {/* Arrows */}
        <IconButton
          aria-label="Previous"
          onClick={prev}
          sx={(t) => ({
            position: "absolute",
            top: "50%",
            left: { xs: 6, md: 16 },
            transform: "translateY(-50%)",
            zIndex: 200,
            bgcolor: alpha(t.palette.background.paper, 0.92),
            border: "1px solid",
            borderColor: "divider",
            "&:hover": { bgcolor: alpha(t.palette.background.paper, 1) },
          })}
        >
          <ChevronLeftIcon />
        </IconButton>
        <IconButton
          aria-label="Next"
          onClick={next}
          sx={(t) => ({
            position: "absolute",
            top: "50%",
            right: { xs: 6, md: 16 },
            transform: "translateY(-50%)",
            zIndex: 200,
            bgcolor: alpha(t.palette.background.paper, 0.92),
            border: "1px solid",
            borderColor: "divider",
            "&:hover": { bgcolor: alpha(t.palette.background.paper, 1) },
          })}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>

      {/* Dots */}
      <Stack direction="row" spacing={0.75} sx={{ mt: 2, justifyContent: "center", alignItems: "center" }}>
        {images.map((_, i) => (
          <IconButton
            key={`s-dot-${i}`}
            size="small"
            onClick={() => goto(i)}
            sx={{ p: 0.5, opacity: i === index ? 1 : 0.55 }}
            aria-label={`Go to slide ${i + 1}`}
          >
            <FiberManualRecordIcon fontSize="inherit" />
          </IconButton>
        ))}
      </Stack>
    </Box>
  );
}

/* =====================================================
   Page
   ===================================================== */

const Anchor = ({ id }) => (
  <span id={id} style={{ position: "relative", top: "-88px", display: "block" }} />
);

const FeatureCard = ({ icon, title, body }) => (
  <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider", height: "100%" }}>
    <Stack spacing={1}>
      <Box sx={{
        width: 40, height: 40, borderRadius: 2,
        display: "flex", alignItems: "center", justifyContent: "center",
        bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
        border: "1px solid", borderColor: (t) => alpha(t.palette.primary.main, 0.2)
      }}>
        {icon}
      </Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
      <Typography variant="body2" color="text.secondary">{body}</Typography>
    </Stack>
  </Paper>
);

const UseCaseCard = ({ icon, title, body }) => (
  <Paper elevation={0} sx={{ p: 2, height: "100%", border: "1px dashed", borderColor: "divider" }}>
    <Stack spacing={1}>
      <Stack direction="row" spacing={1.2} alignItems="center">
        {icon}
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary">{body}</Typography>
    </Stack>
  </Paper>
);

export default function LandingPage() {
  const navigate = useNavigate();
  const toStudio = () => navigate("/studio");
  const handleLogin = () => {
    if (LOGIN_URL) window.location.href = LOGIN_URL;
    else toStudio();
  };
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // 👇 Put your real images in public/screenshots and list them here
  const SCREENSHOTS = useMemo(
    () => ["screenshot1.png", "screenshot2.png", "screenshot3.png", "screenshot4.png", "screenshot5.png"],
    []
  );

  const theme = useTheme();
  const logoSrc = theme.palette.mode === "light" ? "/brand/SemiQolon-Black.png" : "/brand/SemiQolon.png";

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      {/* Header */}
      <AppBar
        position="sticky"
        color="inherit"
        elevation={0}
        sx={{ borderBottom: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}
      >
        <Toolbar sx={{ gap: 1 }}>
          <Box component={RouterLink} to="/" sx={{ display: "flex", alignItems: "center", gap: 1.25, textDecoration: "none" }}>
            <Box component="img" src={logoSrc} alt="SemiQolon Studio" sx={{ height: 32, width: "auto", display: "block" }} loading="eager" />
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <ThemeToggleButton />
          <Button onClick={() => scrollTo("features")} variant="text">Features</Button>
          <Button component={RouterLink} to="/pricing" variant="text">Pricing</Button>
          <Button onClick={() => scrollTo("how")} variant="text">How it works</Button>
          <Button onClick={() => scrollTo("integrations")} variant="text">Integrations</Button>
          <Button onClick={() => scrollTo("usecases")} variant="text">Use cases</Button>
          <Button onClick={() => scrollTo("trust")} variant="text">Trust</Button>
          <Button onClick={handleLogin} variant="contained" sx={{ ml: 1, borderRadius: 999 }}>
            Login
          </Button>
        </Toolbar>
      </AppBar>

      {/* Hero */}
      <Box
        sx={{
          background: (t) =>
            `radial-gradient(1200px 500px at 10% -10%, ${alpha(t.palette.primary.main, 0.20)}, transparent 70%),
             radial-gradient(1200px 500px at 110% 10%, ${alpha(t.palette.secondary.main, 0.18)}, transparent 70%)`,
        }}
      >
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="center">
            <Box sx={{ flex: 1 }}>
              <Chip size="small" color="primary" variant="outlined" label="All your knowledge. One assistant." />
              <Typography variant="h2" sx={{ fontWeight: 900, mt: 2, lineHeight: 1.05 }}>
                Answers from <em>all your</em> data sources — instantly.
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mt: 1.5 }}>
                Integrate with your externl data sources, Upload PDFs, DOCX, PPTX, spreadsheets, images (OCR), and paste URLs. View files <b>in-browser</b>, get cited answers, and keep context.
              </Typography>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 3 }}>
                <Button size="large" variant="contained" onClick={toStudio} startIcon={<BoltIcon />}>
                  Launch Studio
                </Button>
                <Button size="large" variant="outlined" onClick={() => scrollTo("features")}>
                  See how it works
                </Button>
              </Stack>

              <Stack direction="row" spacing={1.25} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                <Chip icon={<CloudUploadIcon />} label="Drag-and-drop files" />
                <Chip icon={<LinkIcon />} label="Train on URLs" />
                <Chip icon={<VisibilityIcon />} label="View in-browser" />
                <Chip icon={<PlayCircleOutlineIcon />} label="Audio/Video ingest" variant="outlined" />
                <Chip icon={<YouTubeIcon />} label="YouTube URLs" variant="outlined" />
                <Chip icon={<SearchIcon />} label="Citations & sources" />
                <Chip icon={<ShieldIcon />} label="Secure by design" />
              </Stack>

              {/* Subtle note: some advanced capabilities need an upgrade (no 'Premium' wording) */}
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                <Chip size="small" icon={<AutoAwesomeIcon />} label="Advanced capabilities" variant="outlined" />
                <Typography variant="caption" color="text.secondary">
                  Media ingest (audio/video) and YouTube URL training available on upgraded plans.
                </Typography>
              </Stack>

              <SpotlightSlider images={SCREENSHOTS} interval={3000} />
            </Box>
          </Stack>
        </Container>
      </Box>

      {/* What’s new */}
      <Container maxWidth="lg" sx={{ pt: { xs: 2, md: 3 }, pb: { xs: 1, md: 2 } }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 2.5 },
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            background: (t) =>
              `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.06)}, ${alpha(t.palette.background.paper, 0.4)})`,
          }}
        >
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip label="New" color="primary" size="small" />
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                Inline viewer, Media ingestion, and YouTube URL training
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              <Chip icon={<VisibilityIcon />} label="Open PDFs/DOCX/PPTX" size="small" />
              <Chip icon={<PlayCircleOutlineIcon />} label="Audio/Video ingest" size="small" variant="outlined" />
              <Chip icon={<YouTubeIcon />} label="YouTube URLs" size="small" variant="outlined" />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                Some advanced features require an upgrade.
              </Typography>
              <Button size="small" component={RouterLink} to="/pricing" variant="outlined" sx={{ ml: 0.5 }}>
                Plans & features
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Container>

      {/* Features */}
      <Anchor id="features" />
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Typography variant="h4" sx={{ fontWeight: 900, textAlign: "center" }}>
          Everything you need to turn documents into answers
        </Typography>
        <Grid container spacing={2.5} sx={{ mt: 1 }}>
          <Grid item xs={12} md={4}>
            <FeatureCard
              icon={<CloudUploadIcon />}
              title="Drop in any file type"
              body={
                <>
                  PDF, DOCX, PPTX, XLSX/CSV, TXT/MD, HTML, images (OCR) — plus <b>audio/video ingest</b>.
                  <br />
                  <Chip size="small" icon={<AutoAwesomeIcon />} label="Advanced" variant="outlined" sx={{ mt: 0.75 }} />
                </>
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FeatureCard
              icon={<LinkIcon />}
              title="Train from URLs"
              body={
                <>
                  Paste docs, knowledge bases, or wikis — we convert pages into searchable text.
                  <br />
                  Add <b>YouTube links</b> to train from videos.
                  <br />
                  <Chip size="small" icon={<AutoAwesomeIcon />} label="Advanced" variant="outlined" sx={{ mt: 0.75 }} />
                </>
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FeatureCard
              icon={<VisibilityIcon />}
              title="View files in-browser"
              body="Open PDFs, Word, slides, images, and more — right inside the app. No downloads or context switching."
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FeatureCard
              icon={<AutoAwesomeIcon />}
              title="Smart instructions"
              body="Write guidelines once (prompts.txt/json). Set tone, role, and rules for consistent answers."
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FeatureCard
              icon={<SearchIcon />}
              title="Cited answers"
              body="Search across everything. Every reply can reference the exact files and links it used."
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FeatureCard
              icon={<ShieldIcon />}
              title="Private by design"
              body="You control access. Fully isolated assistants with complete audit trails."
            />
          </Grid>
        </Grid>
      </Container>

      {/* ================= ADVANCED INTEGRATIONS ================= */}
      <Anchor id="integrations" />
      <Box
        sx={{
          py: { xs: 6, md: 8 },
          background: (t) =>
            `linear-gradient(180deg, ${alpha(t.palette.warning.main, 0.05)}, ${t.palette.background.paper})`,
          borderTop: "1px solid",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Container maxWidth="lg">
          <Stack spacing={2} alignItems="center" textAlign="center">
            <Typography variant="h4" sx={{ fontWeight: 900 }}>
              Integrate. Automate. Empower.
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 720 }}>
              Connect your assistant with <b>APIs</b>, <b>Databases</b>, <b>CRMs</b>, <b>Mailboxes</b>, 
              and <b>Cloud Storage</b> — securely and in real time.
            </Typography>

            <Grid container spacing={2.5} sx={{ mt: 3 }}>
              {[
                {
                  icon: <LinkIcon color="primary" />,
                  title: "APIs",
                  text: "Pull live data from REST, GraphQL, or webhook endpoints.",
                },
                {
                  icon: <StorageIcon color="primary" />,
                  title: "Databases / Warehouses",
                  text: "Connect PostgreSQL, MySQL, MongoDB, or BigQuery securely.",
                },
                {
                  icon: <FeedbackIcon color="primary" />,
                  title: "CRMs",
                  text: "Integrate HubSpot, Salesforce, or Freshdesk for contextual learning.",
                },
                {
                  icon: <MailOutlineIcon color="primary" />,
                  title: "Mailboxes",
                  text: "Read and learn from Gmail, Outlook, or IMAP inboxes.",
                },
                {
                  icon: <CloudQueueIcon color="primary" />,
                  title: "Cloud Storage",
                  text: "Connect AWS S3, Google Cloud Storage, or Azure Blob to auto-train from files.",
                },
                {
                  icon: <SettingsEthernetIcon color="primary" />,
                  title: "Custom Connectors",
                  text: "Link internal APIs, warehouses, or webhooks with your assistants.",
                },
              ].map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item.title}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      border: "1px solid",
                      borderColor: "divider",
                      height: "100%",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        boxShadow: (t) =>
                          `0 4px 18px ${alpha(t.palette.warning.main, 0.25)}`,
                        transform: "translateY(-3px)",
                      },
                    }}
                  >
                    <Stack spacing={1}>
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: (t) => alpha(t.palette.warning.main, 0.1),
                          border: "1px solid",
                          borderColor: (t) => alpha(t.palette.warning.main, 0.2),
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {item.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {item.text}
                      </Typography>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Stack direction="row" spacing={1.5} sx={{ mt: 4 }}>
              <Button
                variant="contained"
                color="warning"
                startIcon={<BoltIcon />}
                onClick={handleLogin}
              >
                Try Integrations
              </Button>
              <Button variant="outlined" onClick={() => scrollTo("trust")}>
                Learn More
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>


      {/* How it works */}
      <Anchor id="how" />
      <Box
        sx={{
          bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
          borderTop: "1px solid",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
          <Typography variant="h4" sx={{ fontWeight: 900, textAlign: "center" }}>
            How it works
          </Typography>

          <Grid container spacing={2.5} sx={{ mt: 1 }}>
            {/* 1 — Upload */}
            <Grid item xs={12} md={3}>
              <FeatureCard
                icon={<CloudUploadIcon color="primary" />}
                title="1 — Upload"
                body="Add PDFs, Word files, slides, spreadsheets, and URLs. We extract and clean content automatically — including OCR for images."
              />
            </Grid>

            {/* 2 — Integrate */}
            <Grid item xs={12} md={3}>
              <FeatureCard
                icon={<SettingsEthernetIcon color="primary" />}
                title="2 — Integrate"
                body="Connect APIs, databases, CRMs, mailboxes, or cloud storage — bring live data right into your assistant’s context."
              />
            </Grid>

            {/* 3 — Train */}
            <Grid item xs={12} md={3}>
              <FeatureCard
                icon={<BoltIcon color="primary" />}
                title="3 — Train"
                body="Click Train. Your assistant learns from all uploaded and integrated data, building a private vector index with your tone and rules."
              />
            </Grid>

            {/* 4 — Chat */}
            <Grid item xs={12} md={3}>
              <FeatureCard
                icon={<PsychologyIcon color="primary" />}
                title="4 — Chat"
                body="Start chatting naturally. Get cited, accurate answers sourced from both your files and connected systems — instantly."
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Use cases */}
      <Anchor id="usecases" />
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Typography variant="h4" sx={{ fontWeight: 900, textAlign: "center" }}>
          Built for document-heavy work
        </Typography>
        <Grid container spacing={2.5} sx={{ mt: 1 }}>
          <Grid item xs={12} md={4}><UseCaseCard icon={<LocalHospitalIcon />} title="Medical" body="Guidelines, EMR exports, discharge notes — quick answers for clinicians and staff." /></Grid>
          <Grid item xs={12} md={4}><UseCaseCard icon={<GavelIcon />} title="Legal" body="Case law, contracts, discovery packets — search and summarize with citations." /></Grid>
          <Grid item xs={12} md={4}><UseCaseCard icon={<AccountBalanceIcon />} title="Finance / Accounting" body="Policies, statements, spreadsheets — reconcile and explain with source links." /></Grid>
          <Grid item xs={12} md={4}><UseCaseCard icon={<SupportAgentIcon />} title="Customer Support" body="Help centers, macros, release notes — keep replies consistent and up to date." /></Grid>
          <Grid item xs={12} md={4}><UseCaseCard icon={<SchoolIcon />} title="Education / Research" body="Papers, slides, lecture notes — extract key points and build study guides." /></Grid>
          <Grid item xs={12} md={4}><UseCaseCard icon={<DescriptionIcon />} title="Operations / HR" body="Policies, SOPs, handbooks — onboard and answer policy questions instantly." /></Grid>
        </Grid>
      </Container>

      {/* Trust & Privacy */}
      <Anchor id="trust" />
      <Container maxWidth="lg" sx={{ pb: { xs: 6, md: 8 } }}>
        <Paper elevation={0} sx={{ p: 3, border: "1px solid", borderColor: "divider" }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="center">
            <ShieldIcon />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>Private by default</Typography>
              <Typography variant="body2" color="text.secondary">
                Your data remains fully secure and accessible only to you. You decide who can upload, train,
                and chat, with complete audit trails for every file, link, and training snapshot.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" onClick={toStudio}>Open Studio</Button>
              <Button component={RouterLink} to="/pricing" variant="text" startIcon={<AutoAwesomeIcon />}>
                Plans & features
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Container>

      {/* Footer */}
      <Box sx={{ borderTop: "1px solid", borderColor: "divider", py: 3 }}>
        <Container maxWidth="lg">
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.5} alignItems="center">
            <Typography variant="body2" color="text.secondary">© {new Date().getFullYear()} Easy Assistant — make documents answer you.</Typography>
            <Stack direction="row" spacing={2}>
              <MUILink href="#" underline="hover" color="inherit" variant="body2">Privacy</MUILink>
              <MUILink href="#" underline="hover" color="inherit" variant="body2">Terms</MUILink>
              <MUILink href="#" underline="hover" color="inherit" variant="body2" onClick={handleLogin}>Login</MUILink>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}
